import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';
import { PromptSection } from '../components/PromptSection';
import { PromptSyntaxContent } from '../components/PromptSyntaxContent';
import { SectionToolbar } from '../components/SectionToolbar';
import { PromptSection as PromptSectionType, SectionData } from '../types';
import { BackupService } from '../services/backupService';
import { HostPersonaApi } from '../../../services/hostPersonaApi';
import { ShowDynamicsApi } from '../../../services/showDynamicsApi';
import { LanguageApi } from '../../../services/languageApi';
import { getShowTranslation } from '../../../data/showTranslation';
import { getStoryTranslation } from '../../../data/story';

// Define the sections based on PRD requirements
const promptSections: PromptSectionType[] = [
  {
    id: 'showIdeas',
    title: '💡 Show: Ideas',
    hasPrompt: true,
    promptConfig: {
      id: 'show_ideas',
      content: '',
      modelConfig: { configText: '' },
    },
    genPromptId: 'show_ideas',
    responseFields: [
      // Genre is an INPUT (moved to a selector above the prompt, like the
      // narrative-form selector on Story Outline) — not a generation output.
      { key: 'selectedShow', label: 'Populate from a show', type: 'dropdown' },
      {
        key: 'topic',
        label: 'Topic',
        type: 'textarea',
        isParam: true,
        paramName: 'show-topic',
      },
    ],
  },
  {
    id: 'show',
    title: '🎭 Show: Profile',
    hasPrompt: true,
    promptConfig: {
      id: 'show_profile',
      content: '',
      modelConfig: { configText: '' },
    },
    genPromptId: 'show_profile',
    responseFields: [
      { key: 'showSlug', label: 'Show Slug', type: 'text' },
      {
        key: 'taxonomy',
        label: 'Taxonomy',
        type: 'text',
        isParam: true,
        paramName: 'show-taxonomy',
      },
      { key: 'itunesCategory', label: 'iTunes Category', type: 'text' },
      { key: 'itunesSubcategory', label: 'iTunes Subcategory', type: 'text' },
      { key: 'storyScope', label: 'Story Scope', type: 'textarea' },
      {
        key: 'titleInstructions',
        label: 'Title Instructions',
        type: 'textarea',
        rows: 2,
      },
      { key: 'style', label: 'Show Style', type: 'textarea' },
      {
        key: 'tags',
        label: 'Show Tags (comma-separated)',
        type: 'text',
        isParam: true,
        paramName: 'showTags',
      },
      { key: 'hostPersona1', label: 'Host Persona 1', type: 'text' },
      {
        key: 'scriptInstructions1',
        label: 'Script Instructions 1',
        type: 'textarea',
        rows: 3,
        isParam: true,
        paramName: 'script-instructions-1',
      },
      {
        key: 'voiceInstructions1',
        label: 'Voice Instructions 1',
        type: 'text',
      },
      { key: 'voiceProfile1', label: 'Voice Profile 1', type: 'text' },
      { key: 'hostPersona2', label: 'Host Persona 2', type: 'text' },
      {
        key: 'scriptInstructions2',
        label: 'Script Instructions 2',
        type: 'textarea',
        rows: 3,
        isParam: true,
        paramName: 'script-instructions-2',
      },
      {
        key: 'voiceInstructions2',
        label: 'Voice Instructions 2',
        type: 'text',
      },
      { key: 'voiceProfile2', label: 'Voice Profile 2', type: 'text' },
      { key: 'dynamics', label: 'Dynamics', type: 'text' },
      { key: 'musicPrompt', label: 'Music Prompt', type: 'textarea' },
      {
        key: 'imagePrompt',
        label: 'Show Image Prompt',
        type: 'textarea',
        isParam: true,
        paramName: 'show-image-prompt',
      },
    ],
  },
  {
    id: 'showTranslation',
    title: '🌐 Show: Translation',
    hasPrompt: true,
    promptConfig: {
      id: 'show_translation',
      content: '',
      modelConfig: { configText: '' },
    },
    genPromptId: 'show_translation',
    // Populated automatically from showIdeas.selectedShow + global language
    responseFields: [
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'searchTerms', label: 'Search Terms', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea', rows: 2 },
      { key: 'audioLogoDesc', label: 'Audio Logo', type: 'text' },
    ],
  },
  {
    id: 'storyIdea',
    title: '📚 Story: Ideas',
    hasPrompt: true,
    promptConfig: {
      id: 'story_ideas',
      content: '',
      modelConfig: { configText: '' },
    },
    genPromptId: 'story_ideas',
    responseFields: [
      { key: 'selectedStory', label: 'Populate from story', type: 'dropdown' },
      {
        key: 'topic',
        label: 'Topic',
        type: 'text',
        isParam: true,
        paramName: 'story-topic',
      },
      {
        key: 'slug',
        label: 'Slug',
        type: 'text',
        isParam: true,
        paramName: 'story-slug',
      },
      { key: 'brief', label: 'Brief', type: 'textarea' },
      {
        key: 'takeaway',
        label: 'Takeaway',
        type: 'textarea',
        rows: 2,
        isParam: true,
        paramName: 'story-takeaway',
      },
      {
        key: 'taxonomy',
        label: 'Taxonomy',
        type: 'text',
        isParam: true,
        paramName: 'story-taxonomy',
      },
      {
        key: 'imagePrompt',
        label: 'Story Image Prompt',
        type: 'textarea',
        rows: 2,
        isParam: true,
        paramName: 'story-image-prompt',
      },
    ],
  },
  // Issue #643: Story outline section
  {
    id: 'storyOutline',
    title: '📐 Story: Outline',
    hasPrompt: true,
    promptConfig: {
      id: 'story_outline',
      content: '',
      modelConfig: { configText: '' },
    },
    genPromptId: 'story_outline',
    // Populated automatically from storyIdea.selectedStory
    responseFields: [
      {
        key: 'outline',
        label: 'Story Outline',
        type: 'textarea',
        rows: 8,
        isParam: true,
        paramName: 'story-outline',
      },
      // #766 Phase 2/3: the outline step also emits the delivery spec
      // (intent/form/specifics/beats). Exposed as {story-delivery-spec} so the
      // story_script prompt can consume it (matches the pipeline param name).
      {
        key: 'delivery_spec',
        label: 'Delivery Spec',
        type: 'textarea',
        rows: 12,
        isParam: true,
        paramName: 'story-delivery-spec',
      },
    ],
  },
  {
    id: 'storyTranslation',
    title: '🌐 Story: Translation',
    hasPrompt: true,
    promptConfig: {
      id: 'story_translation',
      content: '',
      modelConfig: { configText: '' },
    },
    genPromptId: 'story_translation',
    // Populated automatically from storyIdea.selectedStory + global language
    responseFields: [
      {
        key: 'title',
        label: 'Title',
        type: 'text',
      },
      {
        key: 'subtitle',
        label: 'Subtitle',
        type: 'text',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        rows: 3,
      },
      {
        key: 'search_terms',
        label: 'Search Terms',
        type: 'text',
      },
    ],
  },
  {
    id: 'script',
    title: '📝 Story: Script',
    hasPrompt: true,
    promptConfig: {
      id: 'story_script',
      content: '',
      modelConfig: { configText: '' },
    },
    genPromptId: 'story_script',
    responseFields: [{ key: 'script', label: 'Script', type: 'textarea' }],
  },
  {
    id: 'tts',
    title: '🎙️ Story: TTS (Narration)',
    hasPrompt: false,
    genPromptId: 'story_voice',
    responseFields: [
      { key: 'text', label: 'Script Content', type: 'textarea' },
      { key: 'voice-profile-1', label: 'Voice 1 Profile', type: 'text' },
      { key: 'voice-profile-2', label: 'Voice 2 Profile', type: 'text' },
      {
        key: 'voiceInstructions1',
        label: 'Voice 1 Instructions',
        type: 'text',
      },
      {
        key: 'voiceInstructions2',
        label: 'Voice 2 Instructions',
        type: 'text',
      },
      { key: 'audioUrl', label: 'Generated Audio', type: 'audio' },
    ],
  },
  {
    id: 'showAnnounce',
    title: '📢 Show: Announce',
    hasPrompt: true,
    promptConfig: {
      id: 'show_announce',
      content: '',
      modelConfig: { configText: '' },
    },
    genPromptId: 'show_announce',
    responseFields: [
      { key: 'selectedShow', label: 'Show', type: 'dropdown' },
      { key: 'audioUrl', label: 'Generated Announcement', type: 'audio' },
    ],
  },
  {
    id: 'storyAnnounce',
    title: '📢 Story: Announce',
    hasPrompt: true,
    promptConfig: {
      id: 'story_announce',
      content: '',
      modelConfig: { configText: '' },
    },
    genPromptId: 'story_announce',
    responseFields: [
      // No dropdown - uses title from storyTranslation section and name from showTranslation section
      { key: 'audioUrl', label: 'Generated Announcement', type: 'audio' },
    ],
  },
  {
    id: 'imageGeneration',
    title: '🖼️ Show: Image',
    hasPrompt: true,
    promptConfig: {
      id: 'show_image',
      content: '',
      modelConfig: { configText: '' },
    },
    genPromptId: 'show_image',
    responseFields: [
      { key: 'imageUrl', label: 'Generated Image', type: 'image' },
    ],
  },
  {
    id: 'storyImageGeneration',
    title: '🖼️ Story: Image',
    hasPrompt: true,
    promptConfig: {
      id: 'story_image',
      content: '',
      modelConfig: { configText: '' },
    },
    genPromptId: 'story_image',
    responseFields: [
      { key: 'imageUrl', label: 'Generated Image', type: 'image' },
    ],
  },
];

export const PromptDebugConsole: React.FC = () => {
  const [sectionData, setSectionData] = useState<SectionData>({});
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [syntaxDialogOpen, setSyntaxDialogOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | undefined>();
  const [globalLanguage, setGlobalLanguage] = useState<string>('en');
  const [availableLanguages, setAvailableLanguages] = useState<
    Array<{ key: string; name: string }>
  >([]);
  const notify = useNotify();

  // Load available languages
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const languages = await LanguageApi.getAll();
        setAvailableLanguages(
          languages.map(lang => ({ key: lang.key, name: lang.name }))
        );
      } catch (error) {
        console.error('Failed to load languages:', error);
      }
    };
    loadLanguages();
  }, []);

  // Sync global language to sectionData for sections that need it
  useEffect(() => {
    const languageData = availableLanguages.find(l => l.key === globalLanguage);
    if (languageData) {
      setSectionData(prev => ({
        ...prev,
        showIdeas: {
          ...prev.showIdeas,
          selectedLanguage: globalLanguage,
          languageName: languageData.name,
        },
        showTranslation: {
          ...prev.showTranslation,
          selectedLanguage: globalLanguage,
        },
        storyTranslation: {
          ...prev.storyTranslation,
          selectedLanguage: globalLanguage,
        },
      }));
    }
  }, [globalLanguage, availableLanguages]);

  // Auto-populate showTranslation when show is selected + global language changes
  // Fetches the show_translations record for the selected show slug + language
  useEffect(() => {
    const loadShowTranslation = async () => {
      const showSlug = sectionData.show?.showSlug || sectionData.show?.showName;
      if (!showSlug || !globalLanguage) return;

      try {
        const translation = await getShowTranslation(showSlug as string, globalLanguage);
        if (translation) {
          setSectionData(prev => ({
            ...prev,
            showTranslation: {
              ...prev.showTranslation,
              selectedLanguage: globalLanguage,
              name: translation.name || '',
              searchTerms: translation.searchTerms || '',
              description: translation.description || '',
              audioLogoDesc: translation.audioLogoDesc || '',
            },
          }));
        } else {
          // No translation exists yet — clear fields but keep language
          setSectionData(prev => ({
            ...prev,
            showTranslation: {
              ...prev.showTranslation,
              selectedLanguage: globalLanguage,
              name: '',
              searchTerms: '',
              description: '',
              audioLogoDesc: '',
            },
          }));
        }
      } catch (error) {
        console.error('Failed to load show translation:', error);
      }
    };

    loadShowTranslation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionData.show?.showSlug, sectionData.show?.showName, globalLanguage]);

  // Auto-populate storyTranslation and script when story is selected + global language changes
  // Fetches the story_translations record for the selected story slug + language
  useEffect(() => {
    const loadStoryTranslation = async () => {
      const storySlug = sectionData.storyIdea?.slug;
      if (!storySlug || !globalLanguage) return;

      try {
        const translation = await getStoryTranslation(storySlug, globalLanguage);
        if (translation) {
          setSectionData(prev => ({
            ...prev,
            storyTranslation: {
              ...prev.storyTranslation,
              selectedLanguage: globalLanguage,
              title: translation.title || '',
              subtitle: translation.subtitle || '',
              description: translation.description || '',
              search_terms: translation.searchTerms || '',
            },
            script: {
              ...prev.script,
              script: translation.script || '',
            },
          }));
        } else {
          // No translation exists yet — clear fields but keep language
          setSectionData(prev => ({
            ...prev,
            storyTranslation: {
              ...prev.storyTranslation,
              selectedLanguage: globalLanguage,
              title: '',
              subtitle: '',
              description: '',
              search_terms: '',
            },
            script: {
              ...prev.script,
              script: '',
            },
          }));
        }
      } catch (error) {
        console.error('Failed to load story translation:', error);
      }
    };

    loadStoryTranslation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionData.storyIdea?.slug, globalLanguage]);

  // Memoize the prompt sections to prevent unnecessary re-renders
  // Hide instruction fields when personas are set
  const memoizedPromptSections = useMemo(() => {
    return promptSections.map(section => {
      // For show section, hide instruction fields when personas are set
      if (section.id === 'show') {
        const hostPersona1 = sectionData.show?.hostPersona1;
        const hostPersona2 = sectionData.show?.hostPersona2;

        // Filter out instruction fields if corresponding persona is set
        const filteredResponseFields = section.responseFields.filter(field => {
          // Hide scriptInstructions1 and voiceInstructions1 if hostPersona1 is set
          if (
            hostPersona1 &&
            (field.key === 'scriptInstructions1' ||
              field.key === 'voiceInstructions1')
          ) {
            return false;
          }
          // Hide scriptInstructions2 and voiceInstructions2 if hostPersona2 is set
          if (
            hostPersona2 &&
            (field.key === 'scriptInstructions2' ||
              field.key === 'voiceInstructions2')
          ) {
            return false;
          }
          return true;
        });

        return {
          ...section,
          responseFields: filteredResponseFields,
        };
      }
      return section;
    });
  }, [sectionData.show?.hostPersona1, sectionData.show?.hostPersona2]);

  const handleDataChange = useCallback(
    (sectionId: keyof SectionData, data: unknown) => {
      setSectionData(prev => ({
        ...prev,
        [sectionId]: data,
      }));
    },
    []
  );

  // Load personas whenever show data changes and personasList is missing
  // This ensures personas are always available, even when a show is selected
  useEffect(() => {
    const loadPersonas = async () => {
      // Skip if personas already loaded
      if (sectionData.show?.personasList) {
        return;
      }

      try {
        const personasFormatted = await HostPersonaApi.formatForPrompt();
        setSectionData(prev => ({
          ...prev,
          show: {
            ...prev.show,
            personasList: personasFormatted,
          },
        }));
      } catch (error) {
        console.error('Failed to load personas:', error);
      }
    };

    loadPersonas();
  }, [sectionData.show]);

  // Resolve persona instructions when hostPersona1/2 change
  // When show profile generation returns persona keys, auto-populate their instructions
  // When personas are cleared, revert to show's custom instructions
  useEffect(() => {
    const resolvePersonaInstructions = async () => {
      const hostPersona1 = sectionData.show?.hostPersona1;
      const hostPersona2 = sectionData.show?.hostPersona2;

      // Initialize with show's custom instructions (used when personas are null)
      let scriptInstructions1 = sectionData.show?.scriptInstructions1;
      let scriptInstructions2 = sectionData.show?.scriptInstructions2;
      let voiceInstructions1 = sectionData.show?.voiceInstructions1;
      let voiceInstructions2 = sectionData.show?.voiceInstructions2;

      // Only fetch personas if at least one is assigned
      if (hostPersona1 || hostPersona2) {
        try {
          const personas = await HostPersonaApi.getAll();
          const personaMap = new Map(personas.map(p => [p.key, p]));

          // Speaker 1: Use persona instructions if persona assigned, otherwise keep show's custom instructions
          if (hostPersona1) {
            const persona = personaMap.get(hostPersona1);
            if (persona) {
              scriptInstructions1 = persona.scriptInstructions ?? undefined;
              voiceInstructions1 = persona.voiceInstructions;
            }
          }

          // Speaker 2: Use persona instructions if persona assigned, otherwise keep show's custom instructions
          if (hostPersona2) {
            const persona = personaMap.get(hostPersona2);
            if (persona) {
              scriptInstructions2 = persona.scriptInstructions ?? undefined;
              voiceInstructions2 = persona.voiceInstructions;
            }
          }
        } catch (error) {
          console.error('Failed to resolve persona instructions:', error);
          // Keep show's custom instructions on error
        }
      }

      // Always update state to reflect current persona state (whether set or cleared)
      setSectionData(prev => ({
        ...prev,
        show: {
          ...prev.show,
          scriptInstructions1,
          scriptInstructions2,
          voiceInstructions1,
          voiceInstructions2,
        },
      }));
    };

    resolvePersonaInstructions();
    // Intentionally omitting instruction fields from dependencies to avoid infinite loop
    // We only want to re-run when personas change, not when instructions change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionData.show?.hostPersona1, sectionData.show?.hostPersona2]);

  // Load dynamics list whenever show data changes and dynamicsList is missing
  // This ensures dynamics are always available for show profile generation
  useEffect(() => {
    const loadDynamics = async () => {
      // Skip if dynamics already loaded
      if (sectionData.show?.dynamicsList) {
        return;
      }

      try {
        const dynamicsFormatted = await ShowDynamicsApi.formatForPrompt();
        setSectionData(prev => ({
          ...prev,
          show: {
            ...prev.show,
            dynamicsList: dynamicsFormatted,
          },
        }));
      } catch (error) {
        console.error('Failed to load dynamics:', error);
      }
    };

    loadDynamics();
  }, [sectionData.show]);

  // Resolve dynamics instructions when dynamics field changes
  // When show profile generation returns a dynamics key, auto-populate its instructions
  // This is used for story script generation
  useEffect(() => {
    const resolveDynamicsInstructions = async () => {
      const dynamicsKey = sectionData.show?.dynamics;

      // Initialize with empty
      let dynamicsInstructions = '';

      // Only fetch dynamics if key is assigned
      if (dynamicsKey) {
        try {
          const dynamics = await ShowDynamicsApi.getByKey(dynamicsKey);
          if (dynamics) {
            dynamicsInstructions = dynamics.instructions;
          }
        } catch (error) {
          console.error('Failed to resolve dynamics instructions:', error);
        }
      }

      // Always update state to reflect current dynamics state
      setSectionData(prev => ({
        ...prev,
        show: {
          ...prev.show,
          dynamicsInstructions,
        },
      }));
    };

    resolveDynamicsInstructions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionData.show?.dynamics]);

  // Track which section is currently in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id.replace('section-', '');
            setActiveSection(sectionId);
          }
        });
      },
      {
        rootMargin: '-100px 0px -50% 0px',
        threshold: 0,
      }
    );

    // Observe all section elements
    memoizedPromptSections.forEach(section => {
      const element = document.getElementById(`section-${section.id}`);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [memoizedPromptSections]);

  const handleBackup = async () => {
    setIsBackingUp(true);
    try {
      const result = await BackupService.createBackup();

      if (result.success) {
        const dashboardUrl = BackupService.getDashboardUrl();
        notify(
          <div>
            Backup created successfully!{' '}
            <a
              href={dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#90caf9', textDecoration: 'underline' }}
            >
              View in Storage
            </a>
          </div>,
          { type: 'success', multiLine: true }
        );
      } else {
        notify(`Backup failed: ${result.error}`, { type: 'error' });
      }
    } catch (error) {
      notify('An unexpected error occurred during backup', { type: 'error' });
      console.error('Backup error:', error);
    } finally {
      setIsBackingUp(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      <Title title="Prompt Debugging" />

      {/* Header with Language Selector, Syntax and Backup Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          paddingX: 3,
          paddingY: 2,
        }}
      >
        <Select
          size="small"
          value={globalLanguage}
          onChange={e => setGlobalLanguage(e.target.value)}
          sx={{
            fontSize: '0.75rem',
            minWidth: 'auto',
            '& .MuiSelect-select': {
              py: 0.5,
              px: 1,
              minWidth: '24px !important',
            },
          }}
        >
          {availableLanguages.map(lang => (
            <MenuItem key={lang.key} value={lang.key} sx={{ fontSize: '0.75rem' }}>
              {lang.key.toUpperCase()}
            </MenuItem>
          ))}
        </Select>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setSyntaxDialogOpen(true)}
        >
          Syntax
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleBackup}
          disabled={isBackingUp}
        >
          {isBackingUp ? 'Creating Backup...' : 'Backup'}
        </Button>
      </Box>

      {/* Sticky Section Navigation Toolbar */}
      <SectionToolbar
        sections={memoizedPromptSections}
        activeSection={activeSection}
      />

      {/* Sections */}
      <Container
        maxWidth={false}
        sx={{
          paddingBottom: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {memoizedPromptSections.map(section => (
          <Box key={section.id} id={`section-${section.id}`}>
            <PromptSection
              section={section}
              sectionData={sectionData}
              onDataChange={handleDataChange}
              onOpenSyntaxGuide={() => setSyntaxDialogOpen(true)}
            />
          </Box>
        ))}
      </Container>

      {/* Syntax Dialog */}
      <Dialog
        open={syntaxDialogOpen}
        onClose={() => setSyntaxDialogOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>Prompt Template Syntax</DialogTitle>
        <DialogContent>
          <PromptSyntaxContent />
        </DialogContent>
      </Dialog>
    </Box>
  );
};
